import React, {
  Fragment,
  ReactElement,
  useCallback,
  useEffect,
  useState,
} from 'react';
import {
  Box,
  Checkbox,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { VocabularyIrregularVerb, DictionaryWord } from 'app/types';
import { commonTableSx } from 'components/tableStyles';
import dayjs from 'dayjs';
import TableCellContent from 'components/TableCellContent';
import {
  getOrderedVisibleColumnKeys,
  ResolvedTableSettings,
  TableColumnKey,
  TableColumnsVisibility,
} from 'components/tableSettings';

export type RowsSelected = Record<string, boolean>;
export type ColumnFilterKey = 'isActiveSpeech' | 'isLearned' | 'partOfSpeech';
export type ColumnFilters = Partial<Record<ColumnFilterKey, string>>;
export type EditableWordFields = Partial<
  Pick<
    DictionaryWord & VocabularyIrregularVerb,
    | 'definition'
    | 'description'
    | 'isActiveSpeech'
    | 'isLearned'
    | 'translation'
  >
>;

export interface IWordsTableProps {
  words: (Partial<DictionaryWord & VocabularyIrregularVerb> & {
    id: number;
  })[];
  columns: TableColumnsVisibility;
  renderToolbar?: (params: { rowsSelected: RowsSelected }) => ReactElement;
  onRowsSelectedChange?: (rowsSelected: RowsSelected) => void;
  editableRowIds?: number[];
  editedWords?: Record<number, EditableWordFields>;
  onEditedWordChange?: (
    id: number,
    changes: Partial<EditableWordFields>,
  ) => void;
  selectionDisabled?: boolean;
  selectionResetKey?: number;
  columnFilters?: ColumnFilters;
  columnFilterOptions?: Partial<Record<ColumnFilterKey, string[]>>;
  onColumnFilterChange?: (key: ColumnFilterKey, value: string) => void;
  showDescriptionInInfinitive?: boolean;
  tableSettings?: ResolvedTableSettings;
  groupRows?: boolean;
}

// Legacy hand-written column blocks, superseded by the renderHeaderCell /
// renderRecordCell map-based rendering. Kept behind a disabled flag for
// reference rather than deleted outright.
const SHOW_LEGACY_COLUMNS = false;

const WordsTable: React.FC<IWordsTableProps> = ({
  words,
  columns = {},
  renderToolbar,
  onRowsSelectedChange,
  editableRowIds = [],
  editedWords = {},
  onEditedWordChange,
  selectionDisabled = false,
  selectionResetKey = 0,
  columnFilters = {},
  columnFilterOptions = {},
  onColumnFilterChange,
  showDescriptionInInfinitive = false,
  tableSettings,
  groupRows = true,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const learnedColor = theme.palette.primary.vocabularyLearned;
  const activeSpeechColor = theme.palette.primary.vocabularyActiveSpeech;
  const highlightLearned = tableSettings?.highlightLearned ?? true;
  const highlightActiveSpeech = tableSettings?.highlightActiveSpeech ?? true;
  const displayBoolean = (value?: boolean) => (value ? 'yes' : 'no');
  const renderFilterHeader = (
    label: string,
    filterKey: ColumnFilterKey,
    options: { label: string; value: string }[],
  ) =>
    onColumnFilterChange ? (
      <Box sx={{ alignItems: 'center', display: 'flex', gap: 1 }}>
        <Box component={'span'}>{label}</Box>
        <Select
          MenuProps={{ disableScrollLock: true }}
          size={'small'}
          value={columnFilters[filterKey] || 'all'}
          variant={'standard'}
          onChange={(evt: SelectChangeEvent) =>
            onColumnFilterChange(filterKey, evt.target.value)
          }
          sx={{
            color: 'inherit',
            fontSize: 14,
            minWidth: 56,
            '.MuiSelect-select': {
              py: 0,
            },
          }}
        >
          {options.map(({ label: optionLabel, value }) => (
            <MenuItem key={value} value={value}>
              {optionLabel}
            </MenuItem>
          ))}
        </Select>
      </Box>
    ) : (
      label
    );
  const booleanFilterOptions = [
    { label: 'All', value: 'all' },
    { label: 'Yes', value: 'true' },
    { label: 'No', value: 'false' },
  ];
  const visibleColumnKeys = getOrderedVisibleColumnKeys(columns, tableSettings);
  const columnWidths: Partial<Record<TableColumnKey, number | null>> = {
    select: 40,
    number: 64,
    infinitive: 130,
    infinitiveTranscription: 145,
    word: 170,
    transcription: 145,
    translation: columns.definition || columns.description ? 180 : null,
    partOfSpeech: 150,
    definition: null,
    levelBased: 110,
    addedAt: 140,
    pastSimple: 120,
    pastSimpleTranscription: 145,
    pastParticiple: 145,
    pastParticipleTranscription: 165,
    isLearned: 115,
    isActiveSpeech: 145,
    description: null,
  };
  const groupRestColumnSpan = Math.max(visibleColumnKeys.length - 1, 1);
  const renderDescriptionInfo = (value?: string | null) => {
    const descriptionText = value?.trim();

    if (!descriptionText) {
      return null;
    }

    return (
      <Tooltip
        arrow
        placement={'top'}
        slotProps={{
          tooltip: {
            sx: {
              fontSize: 20,
              lineHeight: 1.35,
            },
          },
        }}
        title={descriptionText}
      >
        <InfoOutlinedIcon
          color={'primary'}
          sx={{
            cursor: 'help',
            flexShrink: 0,
            fontSize: 18,
          }}
        />
      </Tooltip>
    );
  };

  const renderHeaderCell = (columnKey: TableColumnKey) => {
    const width = columnWidths[columnKey] ?? undefined;

    switch (columnKey) {
      case 'select':
        return (
          <TableCell key={columnKey} width={width ?? 40}>
            <Checkbox
              color={'primary'}
              disableTouchRipple
              disabled={selectionDisabled}
              checked={selectAllFlag}
              onChange={(evt) => selectAll(evt.target.checked)}
            />
          </TableCell>
        );
      case 'number':
        return (
          <TableCell
            key={columnKey}
            width={width ?? 64}
            sx={{ '&.MuiTableCell-root': { fontSize: 20 } }}
          >
            {'№'}
          </TableCell>
        );
      case 'infinitive':
        return (
          <TableCell
            key={columnKey}
            width={width ?? 130}
            sx={{ '&.MuiTableCell-root': { fontSize: 15 } }}
          >
            {'Infinitive'}
          </TableCell>
        );
      case 'infinitiveTranscription':
        return (
          <TableCell key={columnKey} width={width ?? 145}>
            {'Infinitive Transc.'}
          </TableCell>
        );
      case 'word':
        return (
          <TableCell key={columnKey} width={width ?? 170}>
            {'Word'}
          </TableCell>
        );
      case 'transcription':
        return (
          <TableCell key={columnKey} width={width ?? 145}>
            {'Transcription'}
          </TableCell>
        );
      case 'translation':
        return <TableCell key={columnKey}>{'Translation'}</TableCell>;
      case 'partOfSpeech':
        return (
          <TableCell key={columnKey} width={width ?? 150}>
            {renderFilterHeader('Part of speech', 'partOfSpeech', [
              { label: 'All', value: 'all' },
              ...(columnFilterOptions.partOfSpeech || []).map((value) => ({
                label: value.toLowerCase(),
                value,
              })),
            ])}
          </TableCell>
        );
      case 'definition':
        return <TableCell key={columnKey}>{'Definition'}</TableCell>;
      case 'levelBased':
        return (
          <TableCell key={columnKey} width={width ?? 110}>
            {'Level based'}
          </TableCell>
        );
      case 'addedAt':
        return (
          <TableCell key={columnKey} width={width ?? 140}>
            {'Added at'}
          </TableCell>
        );
      case 'pastSimple':
        return (
          <TableCell key={columnKey} width={width ?? 120}>
            {'Past Simple'}
          </TableCell>
        );
      case 'pastSimpleTranscription':
        return (
          <TableCell key={columnKey} width={width ?? 145}>
            {'Past Simple Transc.'}
          </TableCell>
        );
      case 'pastParticiple':
        return (
          <TableCell key={columnKey} width={width ?? 145}>
            {'Past Participle'}
          </TableCell>
        );
      case 'pastParticipleTranscription':
        return (
          <TableCell key={columnKey} width={width ?? 165}>
            {'Past Participle Transc.'}
          </TableCell>
        );
      case 'isLearned':
        return (
          <TableCell key={columnKey} width={width ?? 115}>
            {renderFilterHeader('Learned', 'isLearned', booleanFilterOptions)}
          </TableCell>
        );
      case 'isActiveSpeech':
        return (
          <TableCell key={columnKey} width={width ?? 145}>
            {renderFilterHeader(
              'Active speech',
              'isActiveSpeech',
              booleanFilterOptions,
            )}
          </TableCell>
        );
      case 'description':
        return <TableCell key={columnKey}>{'Description'}</TableCell>;
      default:
        return null;
    }
  };

  const getGroupingField = useCallback(
    (): keyof (typeof words)[number] =>
      columns.word ? 'word' : columns.infinitive ? 'infinitive' : 'wordType',
    [columns.infinitive, columns.word],
  );

  const getGroupingKey = useCallback(
    (record: (typeof words)[number]) => {
      const value = record[getGroupingField()];

      return typeof value === 'string' ? value[0]?.toLowerCase() || '' : '';
    },
    [getGroupingField],
  );

  const [selectAllFlag, setSelectAllFlag] = useState(false);

  const rowsSelectedInitialState = useCallback(
    (value: boolean = false) =>
      words.reduce<RowsSelected>(
        (acc, { id }) => ({
          ...acc,
          [id]: value,
        }),
        {},
      ),
    [words],
  );

  const [rowsSelected, setRowsSelected] = useState(
    rowsSelectedInitialState(false),
  );

  const selectAll = (value: boolean) => {
    if (selectionDisabled) {
      return;
    }

    const nextRowsSelected = rowsSelectedInitialState(value);

    setSelectAllFlag(value);
    setRowsSelected(nextRowsSelected);
    onRowsSelectedChange?.(nextRowsSelected);
  };

  const selectRow = (id: number) =>
    setRowsSelected((prev) => {
      if (selectionDisabled) {
        return prev;
      }

      const nextRowsSelected = { ...prev, [id]: !prev[id] };
      const rowsSelectedValues = Object.values(nextRowsSelected);

      setSelectAllFlag(
        rowsSelectedValues.length > 0 && rowsSelectedValues.every(Boolean),
      );
      onRowsSelectedChange?.(nextRowsSelected);

      return nextRowsSelected;
    });

  const selectGroup = (groupKey: string) => {
    if (!groupKey || selectionDisabled) {
      return;
    }

    setRowsSelected((prev) => {
      const groupIds = words
        .filter((record) => getGroupingKey(record) === groupKey)
        .map(({ id }) => id);

      if (!groupIds.length) {
        return prev;
      }

      const shouldSelect = !groupIds.every((id) => prev[id]);
      const nextRowsSelected = groupIds.reduce<RowsSelected>(
        (acc, id) => ({
          ...acc,
          [id]: shouldSelect,
        }),
        { ...prev },
      );
      const rowsSelectedValues = Object.values(nextRowsSelected);

      setSelectAllFlag(
        rowsSelectedValues.length > 0 && rowsSelectedValues.every(Boolean),
      );
      onRowsSelectedChange?.(nextRowsSelected);

      return nextRowsSelected;
    });
  };

  const renderRecordCell = (
    columnKey: TableColumnKey,
    record: (typeof words)[number],
    index: number,
    color?: string,
  ) => {
    const {
      word,
      translation,
      transcription,
      partOfSpeech,
      definition,
      isLearned,
      isActiveSpeech,
      id,
      addedAt,
      description,
      infinitive,
      infinitiveTranscription,
      pastParticipleTranscription,
      pastSimpleTranscription,
      pastParticiple,
      pastSimple,
      levelBased,
    } = record;
    const isEditing = editableRowIds.includes(id);
    const editedWord = editedWords[id];
    const dataCellSx =
      columnKey !== 'select' && columnKey !== 'number'
        ? { backgroundColor: color }
        : undefined;

    switch (columnKey) {
      case 'select':
        return (
          <TableCell key={columnKey}>
            <Checkbox
              disableTouchRipple
              disabled={selectionDisabled}
              checked={rowsSelected[id]}
              onChange={() => selectRow(id)}
            />
          </TableCell>
        );
      case 'number':
        return (
          <TableCell key={columnKey}>
            <TableCellContent maxWidth={64}>{index + 1}</TableCellContent>
          </TableCell>
        );
      case 'word':
        return (
          <TableCell key={columnKey} sx={dataCellSx}>
            <TableCellContent>
              {partOfSpeech === 'VERB' ? `to ${word}` : word}
            </TableCellContent>
          </TableCell>
        );
      case 'infinitive':
        return (
          <TableCell key={columnKey} sx={dataCellSx}>
            <Box
              sx={{
                alignItems: 'center',
                display: 'flex',
                gap: 0.5,
                minWidth: 0,
              }}
            >
              <TableCellContent>{infinitive}</TableCellContent>
              {showDescriptionInInfinitive &&
                renderDescriptionInfo(description)}
            </Box>
          </TableCell>
        );
      case 'infinitiveTranscription':
        return (
          <TableCell key={columnKey} sx={dataCellSx}>
            <TableCellContent>{infinitiveTranscription}</TableCellContent>
          </TableCell>
        );
      case 'transcription':
        return (
          <TableCell key={columnKey} sx={dataCellSx}>
            <TableCellContent>{transcription}</TableCellContent>
          </TableCell>
        );
      case 'translation':
        return (
          <TableCell key={columnKey} sx={dataCellSx}>
            {isEditing ? (
              <TextField
                fullWidth
                size={'small'}
                value={editedWord?.translation ?? translation ?? ''}
                variant={'standard'}
                onChange={(evt) =>
                  onEditedWordChange?.(id, {
                    translation: evt.target.value,
                  })
                }
              />
            ) : (
              <TableCellContent>{translation}</TableCellContent>
            )}
          </TableCell>
        );
      case 'partOfSpeech':
        return (
          <TableCell key={columnKey} sx={dataCellSx}>
            <TableCellContent>
              {(partOfSpeech || '').toLowerCase()}
            </TableCellContent>
          </TableCell>
        );
      case 'definition':
        return (
          <TableCell key={columnKey} sx={dataCellSx}>
            {isEditing ? (
              <TextField
                fullWidth
                multiline
                size={'small'}
                value={editedWord?.definition ?? definition ?? ''}
                variant={'standard'}
                onChange={(evt) =>
                  onEditedWordChange?.(id, {
                    definition: evt.target.value,
                  })
                }
              />
            ) : (
              <TableCellContent>{definition}</TableCellContent>
            )}
          </TableCell>
        );
      case 'levelBased':
        return (
          <TableCell key={columnKey} sx={dataCellSx}>
            <TableCellContent>{levelBased}</TableCellContent>
          </TableCell>
        );
      case 'addedAt':
        return (
          <TableCell key={columnKey} sx={dataCellSx}>
            <TableCellContent>
              {addedAt ? dayjs(addedAt).format('DD.MM.YYYY HH:mm') : ''}
            </TableCellContent>
          </TableCell>
        );
      case 'pastSimple':
        return (
          <TableCell key={columnKey} sx={dataCellSx}>
            <TableCellContent>{pastSimple}</TableCellContent>
          </TableCell>
        );
      case 'pastSimpleTranscription':
        return (
          <TableCell key={columnKey} sx={dataCellSx}>
            <TableCellContent>{pastSimpleTranscription}</TableCellContent>
          </TableCell>
        );
      case 'pastParticiple':
        return (
          <TableCell key={columnKey} sx={dataCellSx}>
            <TableCellContent>{pastParticiple}</TableCellContent>
          </TableCell>
        );
      case 'pastParticipleTranscription':
        return (
          <TableCell key={columnKey} sx={dataCellSx}>
            <TableCellContent>
              {pastParticipleTranscription}
            </TableCellContent>
          </TableCell>
        );
      case 'isLearned':
        return (
          <TableCell key={columnKey} sx={dataCellSx}>
            {isEditing ? (
              <Checkbox
                disableTouchRipple
                checked={editedWord?.isLearned ?? !!isLearned}
                onChange={(evt) =>
                  onEditedWordChange?.(id, {
                    isLearned: evt.target.checked,
                  })
                }
              />
            ) : (
              <TableCellContent maxWidth={80}>
                {displayBoolean(isLearned)}
              </TableCellContent>
            )}
          </TableCell>
        );
      case 'isActiveSpeech':
        return (
          <TableCell key={columnKey} sx={dataCellSx}>
            {isEditing ? (
              <Checkbox
                disableTouchRipple
                checked={editedWord?.isActiveSpeech ?? !!isActiveSpeech}
                onChange={(evt) =>
                  onEditedWordChange?.(id, {
                    isActiveSpeech: evt.target.checked,
                  })
                }
              />
            ) : (
              <TableCellContent maxWidth={110}>
                {displayBoolean(isActiveSpeech)}
              </TableCellContent>
            )}
          </TableCell>
        );
      case 'description':
        return (
          <TableCell key={columnKey} sx={dataCellSx}>
            {isEditing ? (
              <TextField
                fullWidth
                multiline
                size={'small'}
                value={editedWord?.description ?? description ?? ''}
                variant={'standard'}
                onChange={(evt) =>
                  onEditedWordChange?.(id, {
                    description: evt.target.value,
                  })
                }
              />
            ) : (
              <TableCellContent>{description}</TableCellContent>
            )}
          </TableCell>
        );
      default:
        return null;
    }
  };

  useEffect(() => {
    const nextRowsSelected = rowsSelectedInitialState(false);

    setSelectAllFlag(false);
    setRowsSelected(nextRowsSelected);
    onRowsSelectedChange?.(nextRowsSelected);
  }, [onRowsSelectedChange, rowsSelectedInitialState, selectionResetKey]);

  const renderMobileCard = (
    record: (typeof words)[number],
    index: number,
  ): ReactElement => {
    const {
      word,
      translation,
      transcription,
      partOfSpeech,
      definition,
      isLearned,
      isActiveSpeech,
      id,
      description,
      infinitive,
      infinitiveTranscription,
      pastSimple,
      pastParticiple,
      levelBased,
    } = record;
    const isVerb = !!infinitive;
    const displayWord = isVerb
      ? infinitive
      : partOfSpeech === 'VERB'
      ? `to ${word ?? ''}`
      : word ?? '';
    const accentColor =
      highlightActiveSpeech && isActiveSpeech
        ? activeSpeechColor
        : highlightLearned && isLearned
        ? learnedColor
        : null;
    const verbForms = [pastSimple, pastParticiple].filter(Boolean).join(' / ');
    const hasStatusRow =
      columns.isLearned ||
      columns.isActiveSpeech ||
      (columns.levelBased && levelBased);

    return (
      <Box
        key={id}
        sx={{
          backgroundColor: 'background.paper',
          border: '1px solid',
          borderColor: 'primary.tableBorder',
          borderRadius: 1,
          borderLeft: accentColor ? `3px solid ${accentColor}` : undefined,
          display: 'flex',
          gap: 1.25,
          p: 1.5,
        }}
      >
        {columns.select && (
          <Checkbox
            checked={!!rowsSelected[id]}
            disableTouchRipple
            disabled={selectionDisabled}
            onChange={() => selectRow(id)}
            size={'small'}
            sx={{ alignSelf: 'flex-start', mt: -0.5, p: 0.5 }}
          />
        )}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box
            sx={{
              alignItems: 'baseline',
              display: 'flex',
              flexWrap: 'wrap',
              gap: 1,
              mb: 0.25,
            }}
          >
            {columns.number && (
              <Typography
                sx={{ color: 'text.tertiary', fontSize: 12, mr: 0.5 }}
              >
                {`#${index + 1}`}
              </Typography>
            )}
            <Typography
              sx={{
                color: 'text.primary',
                fontSize: 16,
                fontWeight: 500,
                wordBreak: 'break-word',
              }}
            >
              {displayWord}
            </Typography>
            {columns.partOfSpeech && partOfSpeech && (
              <Box
                sx={{
                  border: '1px solid',
                  borderColor: 'primary.tableBorder',
                  borderRadius: 0.75,
                  color: 'text.secondary',
                  fontSize: 11,
                  px: 0.75,
                  py: 0.25,
                  textTransform: 'lowercase',
                }}
              >
                {String(partOfSpeech)}
              </Box>
            )}
            {isVerb &&
              showDescriptionInInfinitive &&
              renderDescriptionInfo(description)}
          </Box>
          {((columns.transcription && transcription) ||
            (isVerb && infinitiveTranscription)) && (
            <Typography
              sx={{
                color: 'text.secondary',
                fontFamily: 'monospace',
                fontSize: 13,
                mb: 0.75,
              }}
            >
              {isVerb ? infinitiveTranscription : transcription}
            </Typography>
          )}
          {columns.translation && translation && (
            <Typography
              sx={{
                color: 'text.primary',
                fontSize: 14,
                lineHeight: 1.4,
                mb: 0.75,
                wordBreak: 'break-word',
              }}
            >
              {translation}
            </Typography>
          )}
          {columns.definition && definition && (
            <Typography
              sx={{
                color: 'text.secondary',
                fontSize: 13,
                lineHeight: 1.4,
                mb: 0.75,
                wordBreak: 'break-word',
              }}
            >
              {definition}
            </Typography>
          )}
          {isVerb &&
            verbForms &&
            (columns.pastSimple || columns.pastParticiple) && (
              <Typography
                sx={{
                  color: 'text.secondary',
                  fontFamily: 'monospace',
                  fontSize: 13,
                  mb: 0.75,
                }}
              >
                {verbForms}
              </Typography>
            )}
          {hasStatusRow && (
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 0.75,
                mt: 0.5,
              }}
            >
              {columns.levelBased && levelBased && (
                <Box
                  sx={{
                    border: '1px solid',
                    borderColor: 'primary.tableBorder',
                    borderRadius: 0.75,
                    color: 'text.secondary',
                    fontSize: 11,
                    px: 0.75,
                    py: 0.25,
                  }}
                >
                  {levelBased}
                </Box>
              )}
              {columns.isLearned && isLearned && (
                <Box
                  sx={{
                    backgroundColor: learnedColor,
                    borderRadius: 0.75,
                    color: '#854F0B',
                    fontSize: 11,
                    px: 0.75,
                    py: 0.25,
                  }}
                >
                  {'learned'}
                </Box>
              )}
              {columns.isActiveSpeech && isActiveSpeech && (
                <Box
                  sx={{
                    backgroundColor: activeSpeechColor,
                    borderRadius: 0.75,
                    color: '#3B6D11',
                    fontSize: 11,
                    px: 0.75,
                    py: 0.25,
                  }}
                >
                  {'active'}
                </Box>
              )}
            </Box>
          )}
        </Box>
      </Box>
    );
  };

  if (isMobile) {
    return (
      <>
        {renderToolbar && renderToolbar({ rowsSelected })}
        <Stack spacing={1.25} sx={{ mt: 1 }}>
          {words.map((record, index) => renderMobileCard(record, index))}
        </Stack>
      </>
    );
  }

  return (
    <>
      {renderToolbar && renderToolbar({ rowsSelected })}
      <Box sx={{ maxWidth: '100%', overflowX: 'auto' }}>
        <Table sx={commonTableSx}>
          <colgroup>
            {visibleColumnKeys.map((columnKey) => {
              const width = columnWidths[columnKey];

              return (
                <col
                  key={columnKey}
                  style={width === null ? undefined : { width }}
                />
              );
            })}
          </colgroup>
          <TableHead>
            <TableRow>
              {visibleColumnKeys.map((columnKey) =>
                renderHeaderCell(columnKey),
              )}
              {SHOW_LEGACY_COLUMNS && (
                <>
              {columns.select && (
                <TableCell width={40}>
                  <Checkbox
                    color={'primary'}
                    disableTouchRipple
                    disabled={selectionDisabled}
                    checked={selectAllFlag}
                    onChange={(evt) => selectAll(evt.target.checked)}
                  />
                </TableCell>
              )}
              {columns.number && (
                <TableCell
                  width={64}
                  sx={{ '&.MuiTableCell-root': { fontSize: 20 } }}
                >
                  {'№'}
                </TableCell>
              )}
              {columns.infinitive && (
                <TableCell
                  width={130}
                  sx={{ '&.MuiTableCell-root': { fontSize: 15 } }}
                >
                  {'Infinitive'}
                </TableCell>
              )}
              {columns.infinitiveTranscription && (
                <TableCell width={145}>{'Infinitive Transc.'}</TableCell>
              )}
              {columns.word && <TableCell width={170}>{'Word'}</TableCell>}
              {columns.transcription && (
                <TableCell width={145}>{'Transcription'}</TableCell>
              )}
              {columns.translation && <TableCell>{'Translation'}</TableCell>}
              {columns.partOfSpeech && (
                <TableCell width={150}>
                  {renderFilterHeader('Part of speech', 'partOfSpeech', [
                    { label: 'All', value: 'all' },
                    ...(columnFilterOptions.partOfSpeech || []).map(
                      (value) => ({
                        label: value.toLowerCase(),
                        value,
                      }),
                    ),
                  ])}
                </TableCell>
              )}
              {columns.definition && <TableCell>{'Definition'}</TableCell>}
              {columns.levelBased && (
                <TableCell width={110}>{'Level based'}</TableCell>
              )}
              {columns.addedAt && (
                <TableCell width={140}>{'Added at'}</TableCell>
              )}
              {columns.pastSimple && (
                <TableCell width={120}>{'Past Simple'}</TableCell>
              )}
              {columns.pastSimpleTranscription && (
                <TableCell width={145}>{'Past Simple Transc.'}</TableCell>
              )}
              {columns.pastParticiple && (
                <TableCell width={145}>{'Past Participle'}</TableCell>
              )}
              {columns.pastParticipleTranscription && (
                <TableCell width={165}>{'Past Participle Transc.'}</TableCell>
              )}
              {columns.isLearned && (
                <TableCell width={115}>
                  {renderFilterHeader(
                    'Learned',
                    'isLearned',
                    booleanFilterOptions,
                  )}
                </TableCell>
              )}
              {columns.isActiveSpeech && (
                <TableCell width={145}>
                  {renderFilterHeader(
                    'Active speech',
                    'isActiveSpeech',
                    booleanFilterOptions,
                  )}
                </TableCell>
              )}
                </>
              )}
            </TableRow>

            {/*<TableCell>Active speech</TableCell>*/}
            {/*<TableCell>Added at</TableCell>*/}
          </TableHead>

          <TableBody>
            {words.map((record, index, arr) => {
              const {
                word,
                translation,
                transcription,
                partOfSpeech,
                definition,
                isLearned,
                isActiveSpeech,
                id,
                addedAt,
                description,
                infinitive,
                infinitiveTranscription,
                pastParticipleTranscription,
                pastSimpleTranscription,
                pastParticiple,
                pastSimple,
                levelBased,
              } = record;
              const prevRecord = arr[index - 1];
              const groupingKey = getGroupingKey(record);
              const prevGroupingKey = prevRecord
                ? getGroupingKey(prevRecord)
                : '';
              const isEditing = editableRowIds.includes(id);
              const editedWord = editedWords[id];
              const color = highlightActiveSpeech && isActiveSpeech
                ? activeSpeechColor
                : highlightLearned && isLearned
                ? learnedColor
                : undefined;

              return (
                <Fragment key={id}>
                  {/* grouping row */}
                  {groupRows && (!prevRecord ||
                    (!!groupingKey && prevGroupingKey !== groupingKey)) && (
                    <TableRow>
                      <TableCell
                        onClick={() =>
                          columns.select && selectGroup(groupingKey)
                        }
                        sx={{
                          cursor:
                            columns.select && !selectionDisabled
                              ? 'pointer'
                              : undefined,
                          fontWeight: 700,
                          textTransform: 'uppercase',
                          userSelect: 'none',
                          '&:hover':
                            columns.select && !selectionDisabled
                              ? {
                                  backgroundColor: 'primary.tableHeadBg',
                                }
                              : undefined,
                        }}
                      >
                        {groupingKey}
                      </TableCell>
                      <TableCell colSpan={groupRestColumnSpan} />
                    </TableRow>
                  )}

                  <TableRow>
                    {visibleColumnKeys.map((columnKey) =>
                      renderRecordCell(columnKey, record, index, color),
                    )}
                    {SHOW_LEGACY_COLUMNS && (
                      <>
                    {columns.select && (
                      <TableCell>
                        <Checkbox
                          disableTouchRipple
                          disabled={selectionDisabled}
                          checked={rowsSelected[id]}
                          onChange={() => selectRow(id)}
                        />
                      </TableCell>
                    )}
                    {columns.number && (
                      <TableCell>
                        <TableCellContent maxWidth={64}>
                          {index + 1}
                        </TableCellContent>
                      </TableCell>
                    )}
                    {columns.word && (
                      <TableCell>
                        <TableCellContent>
                          {partOfSpeech === 'VERB' ? `to ${word}` : word}
                        </TableCellContent>
                      </TableCell>
                    )}
                    {columns.infinitive && (
                      <TableCell>
                        <Box
                          sx={{
                            alignItems: 'center',
                            display: 'flex',
                            gap: 0.5,
                            minWidth: 0,
                          }}
                        >
                          <TableCellContent>{infinitive}</TableCellContent>
                          {showDescriptionInInfinitive &&
                            renderDescriptionInfo(description)}
                        </Box>
                      </TableCell>
                    )}
                    {columns.infinitiveTranscription && (
                      <TableCell>
                        <TableCellContent>
                          {infinitiveTranscription}
                        </TableCellContent>
                      </TableCell>
                    )}
                    {columns.transcription && (
                      <TableCell>
                        <TableCellContent>{transcription}</TableCellContent>
                      </TableCell>
                    )}
                    {columns.translation && (
                      <TableCell>
                        {isEditing ? (
                          <TextField
                            fullWidth
                            size={'small'}
                            value={editedWord?.translation ?? translation ?? ''}
                            variant={'standard'}
                            onChange={(evt) =>
                              onEditedWordChange?.(id, {
                                translation: evt.target.value,
                              })
                            }
                          />
                        ) : (
                          <TableCellContent>{translation}</TableCellContent>
                        )}
                      </TableCell>
                    )}
                    {columns.partOfSpeech && (
                      <TableCell>
                        <TableCellContent>
                          {(partOfSpeech || '').toLowerCase()}
                        </TableCellContent>
                      </TableCell>
                    )}
                    {columns.definition && (
                      <TableCell>
                        {isEditing ? (
                          <TextField
                            fullWidth
                            multiline
                            size={'small'}
                            value={editedWord?.definition ?? definition ?? ''}
                            variant={'standard'}
                            onChange={(evt) =>
                              onEditedWordChange?.(id, {
                                definition: evt.target.value,
                              })
                            }
                          />
                        ) : (
                          <TableCellContent>{definition}</TableCellContent>
                        )}
                      </TableCell>
                    )}
                    {columns.levelBased && (
                      <TableCell>
                        <TableCellContent>{levelBased}</TableCellContent>
                      </TableCell>
                    )}
                    {columns.addedAt && (
                      <TableCell>
                        <TableCellContent>
                          {dayjs(addedAt).format('DD.MM.YYYY HH:mm')}
                        </TableCellContent>
                      </TableCell>
                    )}
                    {columns.pastSimple && (
                      <TableCell>
                        <TableCellContent>{pastSimple}</TableCellContent>
                      </TableCell>
                    )}
                    {columns.pastSimpleTranscription && (
                      <TableCell>
                        <TableCellContent>
                          {pastSimpleTranscription}
                        </TableCellContent>
                      </TableCell>
                    )}
                    {columns.pastParticiple && (
                      <TableCell>
                        <TableCellContent>{pastParticiple}</TableCellContent>
                      </TableCell>
                    )}
                    {columns.pastParticipleTranscription && (
                      <TableCell>
                        <TableCellContent>
                          {pastParticipleTranscription}
                        </TableCellContent>
                      </TableCell>
                    )}
                    {columns.isLearned && (
                      <TableCell>
                        {isEditing ? (
                          <Checkbox
                            disableTouchRipple
                            checked={editedWord?.isLearned ?? !!isLearned}
                            onChange={(evt) =>
                              onEditedWordChange?.(id, {
                                isLearned: evt.target.checked,
                              })
                            }
                          />
                        ) : (
                          <TableCellContent maxWidth={80}>
                            {displayBoolean(isLearned)}
                          </TableCellContent>
                        )}
                      </TableCell>
                    )}
                    {columns.isActiveSpeech && (
                      <TableCell>
                        {isEditing ? (
                          <Checkbox
                            disableTouchRipple
                            checked={
                              editedWord?.isActiveSpeech ?? !!isActiveSpeech
                            }
                            onChange={(evt) =>
                              onEditedWordChange?.(id, {
                                isActiveSpeech: evt.target.checked,
                              })
                            }
                          />
                        ) : (
                          <TableCellContent maxWidth={110}>
                            {displayBoolean(isActiveSpeech)}
                          </TableCellContent>
                        )}
                      </TableCell>
                    )}
                    {columns.description && (
                      <TableCell>
                        {isEditing ? (
                          <TextField
                            fullWidth
                            multiline
                            size={'small'}
                            value={editedWord?.description ?? description ?? ''}
                            variant={'standard'}
                            onChange={(evt) =>
                              onEditedWordChange?.(id, {
                                description: evt.target.value,
                              })
                            }
                          />
                        ) : (
                          <TableCellContent>{description}</TableCellContent>
                        )}
                      </TableCell>
                    )}
                      </>
                    )}
                  </TableRow>
                </Fragment>
              );
            })}
          </TableBody>
        </Table>
      </Box>
    </>
  );
};

export default WordsTable;
